import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import Counter from "../components/Counter";
import CTABand from "../components/resource/CTABand";
import { container, item, inView } from "../components/service/presets";
import { CheckCircle2 } from "lucide-react";

export default function WhyUs() {
  const { t } = useLang();
  const w = t.why;

  return (
    <div>
      <PageHero tag={w.hero.tag} title={w.hero.title} desc={w.hero.desc} />

      {/* INTRO + reasons */}
      <section className="py-14 rq-light">
        <div className="rq-container">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">{w.introTitle}</h2>
            <p className="mt-5 text-[color:var(--rq-muted)] leading-relaxed">{w.introDesc}</p>
          </div>
          <motion.div {...inView} variants={container} className="mt-12 grid gap-6 md:grid-cols-3">
            {w.reasons.map((r, i) => (
              <motion.div key={i} variants={item} whileHover={{ y: -8 }} className="rq-card p-8 h-full">
                <CheckCircle2 size={30} className="text-[color:var(--rq-green)]" />
                <h3 className="font-display mt-4 text-lg font-semibold text-white">{r.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[color:var(--rq-muted)]">{r.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="py-14 rq-greendark">
        <div className="rq-container">
          <div className="max-w-3xl">
            <span className="rq-tag"><span className="h-px w-6 bg-[color:var(--rq-green)] inline-block" />{w.workTitle}</span>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold text-white leading-tight">{w.workTitle}</h2>
            <p className="mt-5 text-[color:var(--rq-muted)] leading-relaxed">{w.workDesc}</p>
          </div>
          <motion.div {...inView} variants={container} className="mt-12 grid gap-6 md:grid-cols-3">
            {w.steps.map((s, i) => (
              <motion.div key={i} variants={item} whileHover={{ y: -6 }} className="rq-card p-8 h-full relative overflow-hidden">
                <div className="font-display text-5xl font-bold rq-gradient-text leading-none">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display mt-4 text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[color:var(--rq-muted)]">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* OUR IMPACT */}
      <section className="py-14">
        <div className="rq-container">
          <div className="max-w-3xl">
            <span className="rq-tag"><span className="h-px w-6 bg-[color:var(--rq-green)] inline-block" />{w.impactTag}</span>
            <h2 className="font-display mt-4 text-2xl md:text-[34px] font-bold text-white leading-tight">{w.impactTitle}</h2>
          </div>
          <motion.div {...inView} variants={container} className="mt-10 grid gap-6 sm:grid-cols-3">
            {t.stats.map((s, i) => (
              <motion.div key={i} variants={item} className="rq-card p-8 text-center">
                <div className="font-display text-4xl md:text-5xl font-bold rq-gradient-text">
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 font-semibold text-white">{s.label}</div>
                <div className="mt-1 text-sm text-[color:var(--rq-muted)]">{s.sub}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
